import type { Property } from '~/core/entities/property';

import { shouldUseCompactHeroVideoLayout } from '~/composables/useVideoUrls';
import { EventBusKeys } from '~/core/helpers/symbols';
import { MixpanelProps } from '~/core/service/mixpanel';
import { grammarInflection } from '~~/utils';

import { useEvents } from './events/useEvents';

interface UsePropertyPhotoCounterOptions {
	property: Ref<Property | null>;
}

const NO_VISIBLE_PHOTOS = 0;
const FIRST_MOBILE_PHOTO_COUNT = 1;
const FIRST_DESKTOP_PHOTO_BATCH_COUNT = 4;
const FIRST_VERTICAL_VIDEO_PHOTO_BATCH_COUNT = 3;

export function usePropertyPhotoCounter({
	property
}: UsePropertyPhotoCounterOptions) {
	const { trackPropertyImageScrolled } = useEvents();
	const clickCount = ref<number>(0);

	const desktopCarouselCount = ref<number | undefined>(undefined);
	const offDesktopCount = onClientEventBus(
		useEventBus<number | undefined>(EventBusKeys.desktopHeroSectionPhotoCount),
		count => {
			desktopCarouselCount.value = count;
		}
	);

	const mobileCarouselCount = ref<number | undefined>(undefined);
	const offMobileCount = onClientEventBus(
		useEventBus<number | undefined>(EventBusKeys.mobileHeroSectionPhotoCount),
		count => {
			mobileCarouselCount.value = count;
		}
	);

	watch(desktopCarouselCount, (newCount, oldCount) => {
		if (oldCount !== undefined && newCount !== oldCount) {
			clickCount.value = clickCount.value + 1;

			trackPropertyImageScrolled({
				[MixpanelProps.COMMERCIAL_ID]: property.value?.commercialId!,
				[MixpanelProps.CURRENT_PAGE]: 'Property Details',
				[MixpanelProps.SECTION_NAME]: 'hero section',
				[MixpanelProps.CARD_POSITION]: newCount,
				[MixpanelProps.CARD_TYPE]: 'regular_card',
				[MixpanelProps.COMPANY_NAME]: property.value?.company.name!,
				[MixpanelProps.TAG_EXCLUSIVE]: property.value?.isExclusive!,
				[MixpanelProps.AGENT_NAME]: property.value?.agent.name!,
				[MixpanelProps.CLICK_COUNT]: clickCount.value
			});
		}
	});

	watch(mobileCarouselCount, (newCount, oldCount) => {
		if (oldCount !== undefined && newCount !== oldCount) {
			clickCount.value = clickCount.value + 1;
			trackPropertyImageScrolled({
				[MixpanelProps.COMMERCIAL_ID]: property.value?.commercialId!,
				[MixpanelProps.CURRENT_PAGE]: 'Property Details',
				[MixpanelProps.SECTION_NAME]: 'hero section',
				[MixpanelProps.CARD_POSITION]: newCount,
				[MixpanelProps.CARD_TYPE]: 'regular_card',
				[MixpanelProps.COMPANY_NAME]: property.value?.company.name!,
				[MixpanelProps.TAG_EXCLUSIVE]: property.value?.isExclusive!,
				[MixpanelProps.AGENT_NAME]: property.value?.agent.name!,
				[MixpanelProps.CLICK_COUNT]: clickCount.value
			});
		}
	});

	const numberOfImages = computed<number>(
		() => property.value?.images.length ?? 0
	);

	const numberOfVideos = computed<number>(
		() => property.value?.videos.length ?? 0
	);

	const featuredVideo = computed(() => property.value?.featuredVideos?.[0]);
	const hasFeaturedVideo = computed(() => !!featuredVideo.value?.url);
	const defaultDesktopPhotoCount = computed<number>(() => {
		const totalPhotos = numberOfImages.value;

		if (!totalPhotos) {
			return NO_VISIBLE_PHOTOS;
		}

		if (!hasFeaturedVideo.value) {
			return totalPhotos >= FIRST_DESKTOP_PHOTO_BATCH_COUNT
				? FIRST_DESKTOP_PHOTO_BATCH_COUNT
				: totalPhotos;
		}

		return shouldUseCompactHeroVideoLayout(
			featuredVideo.value?.width,
			featuredVideo.value?.height
		)
			? Math.min(FIRST_VERTICAL_VIDEO_PHOTO_BATCH_COUNT, totalPhotos)
			: NO_VISIBLE_PHOTOS;
	});

	const defaultMobilePhotoCount = computed<number>(() => {
		if (!numberOfImages.value) {
			return NO_VISIBLE_PHOTOS;
		}

		return hasFeaturedVideo.value
			? NO_VISIBLE_PHOTOS
			: FIRST_MOBILE_PHOTO_COUNT;
	});

	// SSR-safe: use default values that match between server and client
	const desktopPhotoModalCtaText = computed<string | undefined>(() => {
		const totalPhotos = numberOfImages.value;

		if (!totalPhotos) {
			return 'Fotos';
		}

		const label = grammarInflection(totalPhotos, 'foto', 'fotos');

		// Use carousel count if available, otherwise use SSR-safe default
		const currentPhotoCount =
			desktopCarouselCount.value ?? defaultDesktopPhotoCount.value;

		if (currentPhotoCount <= NO_VISIBLE_PHOTOS) {
			return 'Fotos';
		}

		return `${currentPhotoCount}/${totalPhotos} ${label}`;
	});

	const mobilePhotoModalCtaText = computed<string | undefined>(() => {
		const totalPhotos = numberOfImages.value;

		if (!totalPhotos) {
			return 'Fotos';
		}

		const label = grammarInflection(totalPhotos, 'foto', 'fotos');

		// Use carousel count if available, otherwise use SSR-safe default (1)
		const currentPhotoCount =
			mobileCarouselCount.value ?? defaultMobilePhotoCount.value;

		if (currentPhotoCount <= NO_VISIBLE_PHOTOS) {
			return 'Fotos';
		}

		return `${currentPhotoCount}/${totalPhotos} ${label}`;
	});

	onUnmounted(() => {
		offDesktopCount();
		offMobileCount();
	});

	return {
		numberOfImages,
		numberOfVideos,
		desktopPhotoModalCtaText,
		mobilePhotoModalCtaText
	};
}
